/**
 * Schedule API — Firestore CRUD for Schedule Events & Rehearsal Scenes
 * 
 * Storage pattern: Subcollections under the project document
 *   organizations/{orgId}/projects/{projectId}/rehearsalSchedule/{eventId}   → ScheduleEvent
 *   organizations/{orgId}/projects/{projectId}/rehearsalScenes/{sceneId}     → RehearsalScene
 * 
 * Migrated from legacy data-document arrays (projects/{id}/data/schedule).
 * This follows the same pattern as feed-api.ts.
 */

import { getBackend } from '@/backend';
import type { Unsubscribe } from '@/backend/contracts';
import type { ScheduleEvent, RehearsalScene } from '../scheduler/types';
import type { ProjectScope } from './project-scope';
import { enqueueStructuredEntityWrite, type StructuredEntityWriteRegistration } from './structured-entity-write';

// ---------------------
// Helpers
// ---------------------

function scheduleClient() {
    const client = getBackend().projectSchedule;
    if (!client) {
        throw new Error('Project schedule backend client is not configured');
    }
    return client;
}

const SCHEDULE_EVENT_WRITE_REGISTRATION: StructuredEntityWriteRegistration = {
    feature: 'schedule',
    capability: 'EDIT_SCHEDULER',
    conflictPrefix: 'schedule',
    allowedKinds: ['patchEntity', 'deleteEntity'],
    authErrorMessage: 'Authentication required to preserve schedule project writes',
};

const REHEARSAL_SCENE_WRITE_REGISTRATION: StructuredEntityWriteRegistration = {
    feature: 'schedule',
    capability: 'EDIT_SCHEDULER',
    conflictPrefix: 'rehearsalScenes',
    allowedKinds: ['patchEntity', 'deleteEntity'],
    authErrorMessage: 'Authentication required to preserve schedule project writes',
};

type AuthorityManagedMetadata = {
    updatedAtMs?: unknown;
    updatedBy?: unknown;
};

function schedulerOwnedSchedulePatch(event: ScheduleEvent): ScheduleEvent {
    const {
        rsvps: _rsvps,
        updatedAtMs: _updatedAtMs,
        updatedBy: _updatedBy,
        ...payload
    } = event as ScheduleEvent & { rsvps?: unknown } & AuthorityManagedMetadata;
    return payload as ScheduleEvent;
}

function schedulerOwnedScenePatch(scene: RehearsalScene): RehearsalScene {
    const {
        updatedAtMs: _updatedAtMs,
        updatedBy: _updatedBy,
        ...payload
    } = scene as RehearsalScene & AuthorityManagedMetadata;
    return payload as RehearsalScene;
}

// ---------------------
// Schedule Event CRUD
// ---------------------

/**
 * Subscribes to schedule events for a project.
 * Returns events ordered by date ascending.
 */
export function subscribeToScheduleEvents(
    scope: ProjectScope,
    onUpdate: (events: ScheduleEvent[]) => void
): Unsubscribe {
    return scheduleClient().subscribeScheduleEvents(scope, onUpdate);
}

export async function listScheduleEvents(scope: ProjectScope): Promise<ScheduleEvent[]> {
    return scheduleClient().listScheduleEvents(scope);
}

/**
 * Saves (creates or updates) a single schedule event.
 */
export async function saveScheduleEvent(scope: ProjectScope, event: ScheduleEvent): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope,
        registration: SCHEDULE_EVENT_WRITE_REGISTRATION,
        entityId: event.id,
        kind: 'patchEntity',
        payload: schedulerOwnedSchedulePatch(event),
    });
}

/**
 * Deletes a schedule event.
 */
export async function deleteScheduleEvent(scope: ProjectScope, eventId: string): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope,
        registration: SCHEDULE_EVENT_WRITE_REGISTRATION,
        entityId: eventId,
        kind: 'deleteEntity',
        payload: { id: eventId },
    });
}

/**
 * Batch-saves multiple schedule events. 
 * Firestore batches limited to 500 ops — no single project should exceed that.
 */
export async function batchSaveScheduleEvents(scope: ProjectScope, events: ScheduleEvent[]): Promise<void> {
    await Promise.all(events.map((event) => saveScheduleEvent(scope, event)));
}

// ---------------------
// Rehearsal Scene CRUD
// ---------------------

/**
 * Subscribes to rehearsal scenes for a project.
 */
export function subscribeToRehearsalScenes(
    scope: ProjectScope,
    onUpdate: (scenes: RehearsalScene[]) => void
): Unsubscribe {
    return scheduleClient().subscribeRehearsalScenes(scope, onUpdate);
}

export async function listRehearsalScenes(scope: ProjectScope): Promise<RehearsalScene[]> {
    return scheduleClient().listRehearsalScenes(scope);
}

/**
 * Saves (creates or updates) a single rehearsal scene.
 */
export async function saveRehearsalScene(scope: ProjectScope, scene: RehearsalScene): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope,
        registration: REHEARSAL_SCENE_WRITE_REGISTRATION,
        entityId: scene.id,
        kind: 'patchEntity',
        payload: schedulerOwnedScenePatch(scene),
    });
}

/**
 * Deletes a rehearsal scene.
 */
export async function deleteRehearsalScene(scope: ProjectScope, sceneId: string): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope,
        registration: REHEARSAL_SCENE_WRITE_REGISTRATION,
        entityId: sceneId,
        kind: 'deleteEntity',
        payload: { id: sceneId },
    });
}
